/**
 * The Drive container a course or session scope reads, shared by the
 * affordance and the closed faces that summarise it.
 */
import { useQuery } from "@tanstack/react-query";

import { ApiError } from "@/lib/api/client";
import { driveContainersApi, driveContainersQueryKey, type DriveContainer } from "@/lib/api/driveContainers";

export type Scope = { kind: "course"; courseId: string } | {
  kind: "session";
  courseId: string;
  sessionId: string;
};

export function containerFor(containers: DriveContainer[], scope: Scope): DriveContainer | undefined {
  const live = containers.filter((item) => item.state !== "failed");
  return scope.kind === "course"
    ? live.find((item) => item.session_id === null)
    : live.find((item) => item.session_id === scope.sessionId);
}

/**
 * A session container needs a ready course container as its parent, and the
 * backend refuses otherwise. Saying so before the click is the difference
 * between an affordance and a trap.
 */
export function parentReady(containers: DriveContainer[]): boolean {
  return containers.some((item) => item.session_id === null && item.state === "ready");
}

export type DriveContainerSummary = "ready" | "pending" | "absent" | "unknown" | "needs-course" | "failed";

/**
 * What the affordance below would draw, from the same read, so a disclosure
 * can say it on its closed face and draw nothing where the affordance draws
 * nothing (a tenant without the capability, or a reader with nothing to open).
 * Only a 404 confirms the capability is absent; any other failed read is
 * named, with its retry, rather than hidden as if there were no folder.
 */
export function useDriveContainerSummary(scope: Scope, canEdit: boolean): { summary: DriveContainerSummary; retry: () => void } | null {
  const containers = useQuery({ queryKey: driveContainersQueryKey(scope.courseId), queryFn: () => driveContainersApi.list(scope.courseId), retry: false });
  const retry = () => void containers.refetch();
  const summary = ((): DriveContainerSummary | null => {
    if (containers.isLoading) return null;
    if (containers.isError) return canEdit && !(containers.error instanceof ApiError && containers.error.status === 404) ? "failed" : null;
    if (!Array.isArray(containers.data?.containers)) return null;
    const all = containers.data.containers, existing = containerFor(all, scope);
    if (existing?.state === "ready" && existing.drive_folder_id) return "ready";
    if (!canEdit) return null;
    if (existing?.state === "pending") return "pending";
    if (existing?.state === "unknown") return "unknown";
    if (scope.kind === "session" && !parentReady(all)) return "needs-course";
    return "absent";
  })();
  return summary && { summary, retry };
}
